// TestReport.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  LinearProgress,
  Alert,
  Divider
} from '@mui/material';
import {
  ArrowBack,
  Print,
  Download,
  LocalHospital,
  Person,
  Assignment,
  Warning,
  CheckCircle
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { resultEntryService } from '../../../../../../src/services/resultEntryService';
import { useLocation } from 'react-router-dom';

export const TestReport = () => {
  const { testId } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [testData, setTestData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadTestReport();
  }, [testId]);

  const loadTestReport = async () => {
    try {
      setLoading(true);
      const response = await resultEntryService.getResultById(testId);

      if (response.success) {
        setTestData(response.data);
      } else {
        setError(response.message || 'Failed to load test report');
      }
    } catch (error) {
      console.error('Error loading test report:', error);
      setError(error.response?.data?.message || 'Failed to load test report');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {

    
  };

  const handleDownload = () => {
    showSnackbar('Download feature coming soon!', 'info');
  };

  const getResultStatusConfig = (status) => {
    const statusConfig = {
      normal: { color: '#10b981', label: 'Normal', bgColor: '#ecfdf5', icon: <CheckCircle /> },
      low: { color: '#f59e0b', label: 'Low', bgColor: '#fffbeb', icon: <Warning /> },
      high: { color: '#ef4444', label: 'High', bgColor: '#fef2f2', icon: <Warning /> },
      critical_low: { color: '#dc2626', label: 'Critical Low', bgColor: '#fef2f2', icon: <Warning /> },
      critical_high: { color: '#dc2626', label: 'Critical High', bgColor: '#fef2f2', icon: <Warning /> },
      abnormal: { color: '#f59e0b', label: 'Abnormal', bgColor: '#fffbeb', icon: <Warning /> }
    };
    return statusConfig[status] || statusConfig.normal;
  };

  const getInterpretation = (result, parameter) => {
    const value = result.numericValue;
    if (value === null || value === undefined) return '';

    const status = result.status;
    const paramName = parameter?.label?.toLowerCase() || 'value';

    const interpretations = {
      normal: `The ${paramName} is within the normal reference range.`,
      low: `The ${paramName} is below the normal reference range. This may require clinical correlation.`,
      high: `The ${paramName} is above the normal reference range. This may require clinical correlation.`,
      critical_low: `CRITICAL: The ${paramName} is critically low. Immediate medical attention is recommended.`,
      critical_high: `CRITICAL: The ${paramName} is critically high. Immediate medical attention is recommended.`,
      abnormal: `The ${paramName} is outside the normal reference range. Clinical correlation is advised.`
    };

    return interpretations[status] || interpretations.normal;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Calculate report summary
  const getReportSummary = () => {
    if (!testData?.service_results) return { total: 0, abnormal: 0, critical: 0 };

    let totalResults = 0;
    let abnormalResults = 0;
    let criticalResults = 0;

    testData.service_results.forEach(service => {
      service.results?.forEach(result => {
        totalResults++;
        if (result.status !== 'normal') abnormalResults++;
        if (result.status === 'critical_low' || result.status === 'critical_high') criticalResults++;
      });
    });

    return { total: totalResults, abnormal: abnormalResults, critical: criticalResults };
  };

  const reportSummary = getReportSummary();

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography variant="h6" align="center" sx={{ mt: 2 }}>
          Loading test report...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          variant="contained"
          startIcon={<ArrowBack />}
          onClick={() => navigate('/hospital/laboratory/result-dashboard')}
        >
          Back to Dashboard
        </Button>
      </Box>
    );
  }

  if (!testData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Test report not found</Alert>
        <Button
          variant="contained"
          startIcon={<ArrowBack />}
          onClick={() => navigate('/hospital/laboratory/result-dashboard')}
          sx={{ mt: 2 }}
        >
          Back to Dashboard
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }} className="print-container">
      {/* Header - Only visible on screen */}
      <Card elevation={3} sx={{ mb: 3 }} className="no-print">
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h4" gutterBottom color="primary">
                Laboratory Test Report
              </Typography>
              <Typography variant="body1" color="textSecondary">
                Official laboratory test results
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<Print />}
              ///  onClick={handlePrint}
                onClick={() =>
                  window.open(`/print/hospital/laboratory/result-entry?refId=${testId}`, '_blank')
                }
              >
                Print Report
              </Button>
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={handleDownload}
              >
                Download PDF
              </Button>
              <Button
                variant="outlined"
                startIcon={<ArrowBack />}
                onClick={() => navigate('/hospital/laboratory/result-dashboard')}
              >
                Back to Dashboard
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Report Content - Optimized for printing */}
      <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }} className="report-card">
        <CardContent sx={{ p: 4 }}>

          {/* Hospital Header - Show in print */}
          <Box sx={{ textAlign: 'center', mb: 4, borderBottom: '3px double', borderColor: 'primary.main', pb: 3 }}>
            <LocalHospital sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
            <Typography variant="h3" color="primary" gutterBottom fontWeight="bold" className="hospital-name">
              MEDICAL LABORATORY CENTER
            </Typography>
            <Typography variant="h6" color="textSecondary" gutterBottom>
              Accredited Medical Diagnostic Laboratory
            </Typography>
            <Typography variant="h5" color="primary.main" fontWeight="bold">
              LABORATORY TEST REPORT
            </Typography>
          </Box>

          {/* Report Summary Alert */}
          {reportSummary.abnormal > 0 && (
            <Alert
              severity={reportSummary.critical > 0 ? "error" : "warning"}
              sx={{ mb: 3, fontSize: '1.1rem', alignItems: 'center' }}
              className="summary-alert"
            >
              <strong>
                {reportSummary.critical > 0 ? 'CRITICAL RESULTS: ' : 'ABNORMAL RESULTS: '}
                {reportSummary.abnormal} of {reportSummary.total} parameters {reportSummary.critical > 0 ? 'require immediate attention' : 'are outside reference range'}
              </strong>
            </Alert>
          )}

          {/* Patient Information */}
          <Paper elevation={0} sx={{ p: 3, mb: 4, border: '1px solid #e0e0e0', backgroundColor: '#fafafa' }} className="patient-info">
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Person sx={{ mr: 2, color: 'primary.main', fontSize: 28 }} />
                  <Typography variant="h5" fontWeight="bold">Patient Information</Typography>
                </Box>
                <Box sx={{ pl: 4 }}>
                  <Typography variant="body1" sx={{ mb: 1 }}><strong>Name:</strong> {testData.patient_name}</Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}><strong>UHID:</strong> {testData.uhid}</Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}><strong>Age/Gender:</strong> {testData.age} • {testData.gender}</Typography>
                  <Typography variant="body1"><strong>Sample Date:</strong> {formatDate(testData.createdAt)}</Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Assignment sx={{ mr: 2, color: 'primary.main', fontSize: 28 }} />
                  <Typography variant="h5" fontWeight="bold">Test Information</Typography>
                </Box>
                <Box sx={{ pl: 4 }}>
                  <Typography variant="body1" sx={{ mb: 1 }}><strong>Report ID:</strong> {testData.barcode_no}</Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}><strong>Collected:</strong> {formatDateTime(testData.createdAt)}</Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}><strong>Referring Doctor:</strong> {testData.doctor?.name || 'N/A'}</Typography>
                  <Typography variant="body1"><strong>Report Generated:</strong> {formatDateTime(new Date())}</Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Test Results */}
          {testData.service_results?.map((serviceResult, index) => (
            <Box key={serviceResult._id} sx={{ mb: 5 }} className="test-section">
              {/* Test Header */}
              <Box sx={{
                backgroundColor: 'primary.main',
                color: 'white',
                p: 2,
                borderRadius: '4px 4px 0 0',
                mb: 0
              }}>
                <Typography variant="h5" fontWeight="bold" align="center">
                  {serviceResult.service?.test_name || 'LABORATORY INVESTIGATION'}
                </Typography>
              </Box>

              {/* Results Table */}
              <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderTop: 'none' }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 'bold', width: '25%', borderRight: '1px solid #e0e0e0' }}>PARAMETER</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '15%', borderRight: '1px solid #e0e0e0' }}>RESULT</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '10%', borderRight: '1px solid #e0e0e0' }}>UNITS</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '20%', borderRight: '1px solid #e0e0e0' }}>REFERENCE RANGE</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>STATUS</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {serviceResult.results?.map((result, idx) => {
                      const statusConfig = getResultStatusConfig(result.status);
                      const parameter = serviceResult.service?.parameters?.find(p => p._id === result.parameter);
                      const interpretation = getInterpretation(result, parameter);

                      return (
                        <React.Fragment key={result._id}>
                          <TableRow
                            sx={{
                              backgroundColor: idx % 2 === 0 ? '#ffffff' : '#fafafa',
                              '&:hover': { backgroundColor: '#f8f9fa' }
                            }}
                            className="result-row"
                          >
                            <TableCell sx={{ borderRight: '1px solid #e0e0e0', fontWeight: 'medium' }}>
                              {parameter?.label || 'Test Parameter'}
                            </TableCell>
                            <TableCell
                              sx={{
                                borderRight: '1px solid #e0e0e0',
                                fontWeight: 'bold',
                                color: statusConfig.color,
                                fontSize: '1.1rem'
                              }}
                            >
                              {result.value || 'N/A'}
                            </TableCell>
                            <TableCell sx={{ borderRight: '1px solid #e0e0e0', color: 'text.secondary' }}>
                              {result.unit || '-'}
                            </TableCell>
                            <TableCell sx={{ borderRight: '1px solid #e0e0e0', color: 'text.secondary' }}>
                              {result.referenceRange || result.referenceRangeText || 'See report'}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={statusConfig.label}
                                sx={{
                                  backgroundColor: statusConfig.bgColor,
                                  color: statusConfig.color,
                                  fontWeight: 'bold',
                                  border: `1px solid ${statusConfig.color}`
                                }}
                                size="small"
                              />
                            </TableCell>
                          </TableRow>

                          {/* Interpretation Row */}
                          {interpretation && (
                            <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                              <TableCell colSpan={5} sx={{ py: 1, px: 2, fontStyle: 'italic', color: statusConfig.color }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Warning sx={{ fontSize: 16, mr: 1 }} />
                                  <strong>Interpretation:</strong> {interpretation}
                                </Box>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Test Metadata */}
              <Box sx={{
                mt: 2,
                p: 2,
                backgroundColor: '#f8f9fa',
                border: '1px solid #e0e0e0',
                borderRadius: '0 0 4px 4px',
                fontSize: '0.9rem'
              }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body2"><strong>Technician:</strong> {serviceResult.technician || 'Lab Technician'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body2"><strong>Instrument:</strong> {serviceResult.instrument || 'Auto Analyzer'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body2"><strong>Method:</strong> {serviceResult.method || 'Standard Method'}</Typography>
                  </Grid>
                  {serviceResult.testedAt && (
                    <Grid item xs={12} md={3}>
                      <Typography variant="body2"><strong>Tested On:</strong> {formatDateTime(serviceResult.testedAt)}</Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </Box>
          ))}

          {/* Report Footer */}
          <Box sx={{
            mt: 4,
            pt: 3,
            borderTop: '2px solid',
            borderColor: 'primary.main',
            textAlign: 'center'
          }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                  <strong>Important Notes:</strong>
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.8rem', textAlign: 'left' }}>
                  • Results should be interpreted in clinical context<br />
                  • Critical values have been communicated to physician<br />
                  • For queries, contact laboratory during working hours<br />
                  • This is an electronically generated report
                </Typography>
              </Grid>
              <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight="bold">
                    Laboratory Director
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    MD, Pathology
                  </Typography>
                </Box>
                <Typography variant="body2" color="textSecondary">
                  Generated on: {formatDateTime(new Date())}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>

      {/* Enhanced CSS for professional printing */}
      <style>
        {`
          @media print {
            .no-print {
              display: none !important;
            }
            .print-container {
              margin: 0;
              padding: 0;
            }
            .report-card {
              box-shadow: none !important;
              border: none !important;
            }
            body {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
              font-family: 'Arial', sans-serif;
              margin: 0;
              padding: 0;
            }
            .hospital-name {
              font-size: 24px !important;
            }
            .summary-alert {
              page-break-inside: avoid;
            }
            .patient-info {
              page-break-inside: avoid;
            }
            .test-section {
              page-break-inside: avoid;
            }
            .result-row {
              page-break-inside: avoid;
            }
            @page {
              margin: 0.5in;
              size: letter;
            }
          }

          /* Screen styles */
          @media screen {
            .result-row:hover {
              background-color: #f8f9fa !important;
            }
          }
        `}
      </style>
    </Box>
  );
};

// Snackbar helper function
const showSnackbar = (message, severity = 'success') => {
  // Implement based on your notification system
  console.log(`${severity}: ${message}`);
};

export default TestReport;